<template>
	<div>
		<div class="sr backdrop-placement" v-observe-visibility="{ callback: onVisibilityPlaybackControl }">
			<div class="sr__container bg-low-active">
				<ClientOnly>
					<video
						ref="videoElement"
						class="title-video__video sr__video-container"
						width="100%"
						height="100%"
						autoplay
						:muted="isMuted"
						playsinline
						@play="() => onVideoStateChanged('play')"
						@loadedmetadata="setVideoDuration"
						@timeupdate="onTimeUpdate"
						@ended="onVideoEnded"
						@imageError="$emit('imageError', sponsoredCampaign?.promotionalVideo?.url)"
					>
						<source :src="sponsoredCampaign?.promotionalVideo?.url || ''" type="video/mp4" />
					</video>
				</ClientOnly>
			</div>
			<ProviderLogoVideoControls
				class="sr__video-controls"
				:isMuted="isMuted"
				:isPaused="isPaused"
				:currentTime="currentTime"
				:videoDuration="videoDuration"
				:showCountDownTimer="showCountDownTimer"
				:showCountdownText="showCountdownText"
				:showProviderIcon="showProviderIcon"
				@toggleMute="toggleMute"
				@togglePlayPause="togglePlayPause"
			>
				<template #provider-icon>
					<slot name="provider-icon" />
				</template>
			</ProviderLogoVideoControls>
			<PlayerProgressBar
				:currentTime="currentTime"
				:videoDuration="videoDuration"
				:showPlayerProgress="showPlayerProgress"
			/>
			<SRCompanion
				:sponsoredAd="sponsoredAd"
				@imageError="imageError"
				@onPosterClickOut="onPosterClickOut"
				class="sr__companion"
				:hideContent="hideContent"
			>
				<template #action-items>
					<slot name="action-items" />
				</template>
			</SRCompanion>
		</div>
	</div>
</template>

<script setup lang="ts">
import type { Maybe } from '@/helpers/types';
import ProviderLogoVideoControls from '@/features/sponsored-recommendations/components/ProviderLogoVideoControls.vue';
import PlayerProgressBar from '@/features/sponsored-recommendations/components/PlayerProgressBar.vue';
import { computed, onMounted, ref, watch } from 'vue';
import { useDocumentVisibility } from '@vueuse/core';
import { SnowplowContext } from '@/helpers/tracking/providers/snowplow-contexts';

import { SraEventAction, SraPlacement } from '@/@types/graphql-types';

import type { SponsoredAdFragment } from '@/features/sponsored-recommendations/graphql/fragments/SponsoredAd.fragment';
import type { SingleTitleSR } from '@/features/sponsored-recommendations/types';

import SRCompanion from '@/features/sponsored-recommendations/components/SRCompanion.vue';
import { useBackendTracking } from '@/helpers/composables/useBackendTracking';

/** Viewing thresholds in percentages at which we want to track. */
type TrackingWatchedPointsInPercentage = (typeof TrackingWatchedPointsInPercentage)[number];
const TrackingWatchedPointsInPercentage = [0, 25, 50, 75, 90, 100] as const;

/** Viewing thresholds in seconds at which we want to track. */
type TrackingWatchedPointsInSeconds = (typeof TrackingWatchedPointsInSeconds)[number];
const TrackingWatchedPointsInSeconds = [2, 6, 15] as const;

const props = withDefaults(
	defineProps<{
		sponsoredAd: SponsoredAdFragment;
		isMuted?: boolean;
		lazy?: boolean;
		showOverlay?: boolean;
		additionalContexts?: SnowplowContext[];
		sraPlacement?: SraPlacement;
		showPlayerProgress?: boolean;
		showCountDownTimer?: boolean;
		showCountdownText?: boolean;
		showProviderIcon?: boolean;
		hideContent?: boolean;
	}>(),
	{
		lazy: true,
		isMuted: false,
		showOverlay: false,
		additionalContexts: () => [],
	}
);

const emits = defineEmits<{
	(e: 'onPosterClickOut'): void;
	(e: 'imageError', src: Maybe<string>): void;
	(e: 'onVideoEnded', hasVideoEnded: boolean): void;
}>();

function imageError(src: Maybe<string>) {
	emits('imageError', src);
}

function onPosterClickOut() {
	emits('onPosterClickOut');
}

const { trackSraEvent } = useBackendTracking();
const visibility = useDocumentVisibility();

const sponsoredCampaign = computed(() => props.sponsoredAd?.campaign);
const campaignNode = computed<SingleTitleSR | null>(
	() => (sponsoredCampaign.value?.node ?? null) as SingleTitleSR | null
);

/* VIDEO TRACKING */
const videoElement = ref<HTMLVideoElement | null>(null);

/* Watch Document Visibility */
watch(visibility, (current, previous) => {
	if (videoElement && current === 'visible' && previous === 'hidden') {
		videoElement.value?.play();
		return;
	}

	if (videoElement && current === 'hidden' && previous === 'visible') {
		videoElement.value?.pause();
		return;
	}
});

/* VIDEO CONTROL*/
const isPaused = ref(false);
const isMuted = ref(true);
const currentTime = ref(0);

watch(isMuted, newMuted => {
	if (videoElement.value) {
		videoElement.value.muted = newMuted;
	}
});

watch(isPaused, newPaused => {
	if (videoElement.value) {
		if (newPaused) {
			videoElement.value.pause();
		} else {
			videoElement.value.play();
		}
	}
});

function onVideoStateChanged(eventName: string) {
	if (eventName !== 'play') return;
	setTimeout(() => {
		if (!videoElement.value) return;
		isPaused.value ? videoElement.value.pause() : videoElement.value.play();
	}, 0);
}

function onVisibilityPlaybackControl(isVisible: boolean) {
	if (!videoElement.value) return;
	isVisible ? videoElement.value.play() : videoElement.value.pause();
}

function toggleMute() {
	isMuted.value = !isMuted.value;

	if (videoElement.value) {
		videoElement.value.muted = isMuted.value;
	}

	if (!campaignNode.value) {
		return;
	}

	trackSraEvent({
		action: SraEventAction.TrailerToggleMute,
		sponsoredAd: () => props.sponsoredAd,
		progress: isMuted.value ? 1 : 0,
	});
}

function togglePlayPause() {
	isPaused.value = !isPaused.value;

	if (!videoElement.value) return;

	isPaused.value ? videoElement.value.pause() : videoElement.value.play();

	if (!campaignNode.value) {
		return;
	}

	// Track the play/pause event only for Backdrop placement
	trackSraEvent({
		action: SraEventAction.TrailerTogglePlay,
		sponsoredAd: () => props.sponsoredAd,
		progress: isPaused.value ? 1 : 0,
	});
}

/* Track Duration Play */
const videoDuration = ref(0);
const lastTrackedPointPercentage = ref<TrackingWatchedPointsInPercentage | -1>(-1);
const lastTrackedPointSeconds = ref<TrackingWatchedPointsInSeconds | -1>(-1);

function setVideoDuration() {
	if (!videoElement.value) return;
	videoDuration.value = videoElement.value.duration;
}

function onTimeUpdate() {
	currentTime.value = videoElement.value?.currentTime ?? 0;

	/** Percentage Tracking */
	if (lastTrackedPointPercentage.value < 100 && videoDuration.value > 0) {
		const percentageViewed = Math.ceil((currentTime.value / videoDuration.value) * 100);
		const nextTrackingPointPercent = TrackingWatchedPointsInPercentage.find(
			point => point > lastTrackedPointPercentage.value
		);

		if (nextTrackingPointPercent != null && percentageViewed >= nextTrackingPointPercent) {
			lastTrackedPointPercentage.value = nextTrackingPointPercent;

			trackSraEvent({
				action: SraEventAction.VideoView,
				sponsoredAd: () => props.sponsoredAd,
				progress: nextTrackingPointPercent,
			});
		}
	}

	/** Seconds Tracking */
	if (lastTrackedPointSeconds.value < 15) {
		const secondsViewed = Math.floor(currentTime.value);
		const nextTrackingPointSeconds = TrackingWatchedPointsInSeconds.find(
			point => point > lastTrackedPointSeconds.value
		);

		if (nextTrackingPointSeconds != null && secondsViewed >= nextTrackingPointSeconds) {
			lastTrackedPointSeconds.value = nextTrackingPointSeconds;

			trackSraEvent({
				action: SraEventAction.VideoViewSeconds,
				sponsoredAd: () => props.sponsoredAd,
				progress: nextTrackingPointSeconds,
			});
		}
	}
}

/**
 * `this.onTimeUpdate` doesn't always fire at 100 before restarting the loop.
 * So we trigger it manually to guarantee it gets tracked.
 */
function onVideoEnded() {
	if (!videoElement.value) return;

	emits('onVideoEnded', true);

	// Replay the video
	//videoElement.value.play();
}

// Set the initial muted state based on the prop
onMounted(() => {
	if (props.sraPlacement === 'BACKDROP_VIDEO') {
		isMuted.value = false;
		if (videoElement.value) {
			videoElement.value.muted = false;
		}
	}
});
</script>

<style lang="scss" scoped>
.sr {
	position: relative;
	height: 100%;
	width: 100%;

	:deep(.control-bar) {
		padding: 0px 16px !important;
	}

	:deep(.video-progress-container) {
		padding: 0px 16px !important;
	}

	&__container {
		width: 100%;
		height: 100%;
		position: relative;
		display: flex;
		overflow: hidden;
		transition: opacity 0.1s ease;
		aspect-ratio: 16/9;
	}

	&__mute-control {
		position: absolute;
		z-index: 10;
		cursor: pointer;
		top: var(--jw-spacing-2);
		background-color: rgba(47, 56, 65, 0.7);
		width: 52px;
		height: 50px;
		display: flex;
		justify-content: center;
	}

	&__overlay {
		display: flex;
		flex-direction: column;
		justify-content: flex-end;
		opacity: 1;
		transition: opacity 2s ease-in-out;

		position: absolute;
		z-index: 1;
		top: 0;
		width: 100%;
		height: 100%;

		&:hover {
			transition: opacity 0.25s ease-in-out;
		}

		&--fading {
			opacity: 0;
		}

		// Apply gradient for gif campaigns to show disclaimer text
		&--gradient {
			background: linear-gradient(
				to bottom,
				rgba(0, 0, 0, 0) 0%,
				rgba(0, 0, 0, 0.02) 15%,
				rgba(0, 0, 0, 0.08) 25%,
				rgba(0, 0, 0, 0.2) 35%,
				rgba(0, 0, 0, 0.4) 50%,
				rgba(0, 0, 0, 0.6) 65%,
				rgba(0, 0, 0, 0.75) 80%,
				rgba(0, 0, 0, 0.85) 90%,
				rgba(0, 0, 0, 0.8) 100%
			);
		}
	}

	&__disclaimer {
		font-size: 10px;
		color: var(--jw-shade-white);
	}
	&__video-container {
		object-fit: cover;
		aspect-ratio: 16/9;
		&--gif-top {
			object-position: center 9%;
		}
	}
	&__content-container {
		display: flex;
		flex-direction: column;
	}
	&__companion {
		cursor: pointer;
	}
}

//remove opacity on ios devices as it causes flickering
html.ios .sr__video:active {
	opacity: unset;
}
</style>
